import { useEffect, useState } from 'react';
import * as Cesium from 'cesium';
import { Input } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import './MapSearch.scss';

class CustomGeocoder {
  credit = new Cesium.Credit('Custom Lat/Lon Geocoder');

  async geocode(query: string) {
    const regex = /^\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*$/;
    const match = query.match(regex);
    if (match) {
      const lat = parseFloat(match[1]);
      const lon = parseFloat(match[3]);
      const cartesian = Cesium.Cartesian3.fromDegrees(lon, lat);
      return [
        {
          type: 'coordinate',
          displayName: `Lat: ${lat}, Lon: ${lon}`,
          destination: cartesian,
        },
      ];
    }
    return [];
  }
}

const MapSearch = ({ searchableFeatures, viewerRef }: any) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const performSearch = async (term: string) => {
    if (!term || !viewerRef.current) {
      setSearchResults([]);
      return;
    }

    const results: any[] = [];

    // Dataset search
    const datasetMatches = searchableFeatures
      .filter((feature: any) => {
        const lowercaseTerm = term.toLowerCase();
        return Object.values(feature.properties).some((value) => String(value).toLowerCase().includes(lowercaseTerm));
      })
      .map((f: any) => ({ ...f, type: 'dataset' }));
    results.push(...datasetMatches);

    // Address search
    try {
      const geocoder = new Cesium.IonGeocoderService({ scene: viewerRef.current.scene });
      const addressResults = await geocoder.geocode(term);
      results.push(...addressResults.map((r: any) => ({ ...r, type: 'address' })));
    } catch (e) {
      console.warn('Address geocode failed', e);
    }

    // Coordinate search
    const customGeocoder = new CustomGeocoder();
    const coordResults = await customGeocoder.geocode(term);
    results.push(...coordResults);

    setSearchResults(results);
  };

  const zoomToResult = (result: any) => {
    if (!viewerRef.current) return;
    const viewer = viewerRef.current;

    viewer.entities.removeAll();

    if (result.type === 'dataset') {
      viewerRef.current.entities.removeAll();

      const geom = result.geometry;

      const extractPositions = (geometry: any) => {
        switch (geometry.type) {
          case 'Point':
            return [Cesium.Cartesian3.fromDegrees(geometry.coordinates[0], geometry.coordinates[1])];

          case 'MultiPoint':
            return geometry.coordinates.map((c: any) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));

          case 'LineString':
            return geometry.coordinates.map((c: any) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));

          case 'MultiLineString':
            return geometry.coordinates.flat(1).map((c: any) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));

          case 'Polygon':
            return geometry.coordinates[0].map((c: any) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));

          case 'MultiPolygon':
            return geometry.coordinates.flat(2).map((c: any) => Cesium.Cartesian3.fromDegrees(c[0], c[1]));

          default:
            return [];
        }
      };

      const positions = extractPositions(geom);
      if (positions.length === 0) return;

      if (geom.type === 'Point' || geom.type === 'MultiPoint') {
        positions.forEach((pos: any) => {
          viewerRef.current.entities.add({
            position: pos,
            point: {
              pixelSize: 12,
              color: Cesium.Color.YELLOW,
              outlineColor: Cesium.Color.BLACK,
              outlineWidth: 2,
            },
          });
        });
      } else if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
        viewerRef.current.entities.add({
          polyline: {
            positions,
            width: 3,
            material: Cesium.Color.YELLOW,
          },
        });
      } else {
        viewerRef.current.entities.add({
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(positions),
            material: Cesium.Color.YELLOW.withAlpha(0.5),
            outline: true,
            outlineColor: Cesium.Color.RED,
            outlineWidth: 2,
          },
        });
      }

      if (positions.length === 1) {
        const boundingSphere = new Cesium.BoundingSphere(positions[0], 0);
        viewer.camera.flyToBoundingSphere(boundingSphere, {
          offset: new Cesium.HeadingPitchRange(viewer.camera.heading, Cesium.Math.toRadians(-45), 2500),
          duration: 2,
        });
      } else {
        const bounding = Cesium.Rectangle.fromCartesianArray(positions);

        if (Math.abs(bounding.east - bounding.west) < 0.000001) {
          const boundingSphere = new Cesium.BoundingSphere(positions[0], 0);
          viewer.camera.flyToBoundingSphere(boundingSphere, {
            offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), 2500),
            duration: 2,
          });
        } else {
          viewer.camera.flyTo({
            destination: bounding,
            duration: 2,
          });
        }
      }
    } else {
      if (result.boundingBox) {
        viewerRef.current.camera.flyTo({ destination: result.boundingBox });
      } else if (result.destination instanceof Cesium.Rectangle) {
        viewerRef.current.camera.flyTo({ destination: result.destination, duration: 2 });
      } else if (result.destination instanceof Cesium.Cartesian3) {
        const carto = Cesium.Cartographic.fromCartesian(result.destination);
        viewerRef.current.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude), 3000),
          duration: 2,
        });
      }

      viewerRef.current.entities.removeAll();
      let pinPosition = result.destination;
      if (pinPosition instanceof Cesium.Rectangle) {
        const center = Cesium.Rectangle.center(pinPosition);
        pinPosition = Cesium.Cartesian3.fromRadians(center.longitude, center.latitude);
      }
      viewerRef.current.entities.add({
        position: pinPosition,
        point: {
          pixelSize: 12,
          color: Cesium.Color.RED,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
        },
      });
    }

    setSearchResults([]);
    setSearchTerm('');
    setIsExpanded(false);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => performSearch(searchTerm), 400);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const getDisplayValues = (properties: any) => {
    const priorityFields = ['desc', 'address', 'identity', 'resident', 'layer'];
    for (let field of priorityFields) {
      if (properties[field]) return properties[field];
    }
    for (let [key, value] of Object.entries(properties)) {
      if (value && typeof value !== 'object') {
        return `${key}: ${value}`;
      }
    }
    return 'Unnamed Feature';
  };

  return (
    <div className="map-search-container">
      <div className={`search-box ${isExpanded ? 'expanded' : ''}`}>
        {!isExpanded ? (
          <SearchOutlined className="search-icon" onClick={() => setIsExpanded(true)} />
        ) : (
          <>
            <Input
              autoFocus
              placeholder="Search address, dataset or coordinates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <CloseOutlined
              className="close-icon"
              onClick={() => {
                setSearchTerm('');
                setSearchResults([]);
                setIsExpanded(false);
              }}
            />
          </>
        )}
      </div>

      {searchResults.length > 0 && (
        <div className="results-dropdown">
          {searchResults.map((result, index) => (
            <div key={index} onClick={() => zoomToResult(result)} className="result-item">
              <strong>{result.type === 'dataset' ? getDisplayValues(result.properties) : result.displayName}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapSearch;
